import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../utils/cookies";
import { useLoading } from "../context/LoadingContext";

function maskEmail(email) {
  const [user, domain] = email.split("@");
  const maskedUser = user.length > 2 ? user.slice(0, 2) + "***" : user[0] + "***";
  return `${maskedUser}@${domain}`;
}

const ConfirmEmail = () => {
  const { startLoading, stopLoading } = useLoading();
  const [maskedEmail, setMaskedEmail] = useState("");
  const [enteredEmail, setEnteredEmail] = useState("");
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const navigate = useNavigate();

  const emailHasAt = enteredEmail.includes("@");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enteredEmail);

  useEffect(() => {
    const email = localStorage.getItem("unverifiedEmail");
    if (!email) { navigate("/login"); return; }
    fetch("https://awaretherapycenter.ro/accounts/csrf-verification/", { credentials: "include" });
    setMaskedEmail(maskEmail(email));
    requestAnimationFrame(() => setTimeout(() => setLoaded(true), 50));
  }, [navigate]);

  const sendVerificationRequest = async (email) => {
    startLoading();
    try {
      const response = await fetch("https://awaretherapycenter.ro/accounts/send-code/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRFToken": getCookie("csrftoken") },
        credentials: "include",
        body: new URLSearchParams({ email }),
      });
      const data = await response.json();
      if (response.ok) { localStorage.setItem("emailConfirmed", "true"); navigate("/register-verify"); }
      else setError(data.error || "A apărut o eroare.");
    } catch (err) { setError("Eroare de rețea. Încearcă din nou."); }
    finally { stopLoading(); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!emailValid) { setError("Introdu o adresă de email validă."); return; }
    const storedEmail = localStorage.getItem("unverifiedEmail");
    if (enteredEmail.trim().toLowerCase() === storedEmail?.toLowerCase()) {
      sendVerificationRequest(enteredEmail);
    } else {
      setError("Emailul introdus nu se potrivește cu cel înregistrat.");
    }
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Inter:wght@300;400;500&display=swap');

    .ce-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(250,247,242,0.92); backdrop-filter: blur(30px); -webkit-backdrop-filter: blur(30px); }
    .ce-nav-inner { max-width: 1400px; margin: 0 auto; padding: 28px 48px; display: flex; align-items: center; justify-content: space-between; }
    .ce-nav-logo { font-family: 'Cormorant', Georgia, serif; font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: #074F4F; text-decoration: none; }
    .ce-nav-links { display: flex; gap: 44px; list-style: none; padding: 0; margin: 0; }
    .ce-nav-links a { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: #2d2d2d; text-decoration: none; transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); position: relative; padding-bottom: 4px; }
    .ce-nav-links a::after { content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 1px; background: #074F4F; transform: scaleX(0); transform-origin: right; transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
    .ce-nav-links a:hover::after { transform: scaleX(1); transform-origin: left; }
    .ce-nav-links a:hover { color: #074F4F; }

    .ce-grain { position: fixed; inset: 0; z-index: 10; pointer-events: none; opacity: 0.02; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); background-size: 128px; }

    .ce-scene { min-height: 100vh; padding-top: 100px; padding-bottom: 60px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; background: #FAF7F2; }
    .ce-scene::before { content: ''; position: absolute; top: -20%; right: -10%; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(218,235,219,0.25) 0%, transparent 70%); pointer-events: none; }
    .ce-scene::after { content: ''; position: absolute; bottom: -15%; left: -8%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(245,225,208,0.2) 0%, transparent 70%); pointer-events: none; }

    .ce-content { position: relative; z-index: 2; max-width: 520px; width: 100%; padding: 0 24px; text-align: center; opacity: 0; transform: translateY(40px); transition: all 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s; }
    .ce-scene.loaded .ce-content { opacity: 1; transform: translateY(0); }

    .ce-icon { width: 64px; height: 64px; border-radius: 50%; border: 1.5px solid #C6A769; display: flex; align-items: center; justify-content: center; margin: 0 auto 32px; color: #C6A769; }

    .ce-heading { font-family: 'Cormorant', Georgia, serif; font-size: 36px; font-weight: 300; color: #074F4F; letter-spacing: -0.02em; margin-bottom: 12px; }
    .ce-sub { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 300; color: #8a8a8a; line-height: 1.6; margin-bottom: 36px; }
    .ce-sub strong { font-weight: 500; color: #074F4F; }

    .ce-card { background: #fff; padding: 40px 36px; text-align: left; box-shadow: 0 1px 3px rgba(7,79,79,0.02), 0 8px 32px rgba(7,79,79,0.04), 0 24px 60px rgba(7,79,79,0.03); position: relative; }
    .ce-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: #C6A769; transform: scaleX(0); transform-origin: left; transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.6s; }
    .ce-scene.loaded .ce-card::before { transform: scaleX(1); }

    .ce-label { display: block; font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; color: #074F4F; margin-bottom: 10px; }
    .ce-input { width: 100%; height: 52px; padding: 0 18px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 300; color: #2d2d2d; background: #FAF7F2; border: 1px solid transparent; border-radius: 0; outline: none; transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); -webkit-appearance: none; }
    .ce-input:focus { background: #fff; border-color: #C6A769; box-shadow: 0 0 0 3px rgba(198,167,105,0.06); }
    .ce-input::placeholder { color: #b5b5b5; font-weight: 300; }

    .ce-checks { display: flex; flex-direction: column; gap: 5px; margin-top: 10px; }
    .ce-check { display: flex; align-items: center; gap: 8px; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 400; color: #b5b5b5; transition: color 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .ce-check.valid { color: #5a9a6a; }
    .ce-check-icon { width: 15px; height: 15px; border-radius: 50%; border: 1.5px solid #d4d4d4; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .ce-check.valid .ce-check-icon { border-color: #5a9a6a; background: #5a9a6a; }
    .ce-check-icon svg { width: 9px; height: 9px; opacity: 0; transform: scale(0.5); transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .ce-check.valid .ce-check-icon svg { opacity: 1; transform: scale(1); }

    .ce-error { font-family: 'Inter', sans-serif; font-size: 12px; color: #b33; background: rgba(187,51,51,0.04); border-left: 2px solid #C6A769; padding: 12px 16px; margin-top: 16px; line-height: 1.5; }

    .ce-submit { width: 100%; height: 52px; margin-top: 24px; font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 3.5px; text-transform: uppercase; color: #074F4F; background: #C6A769; border: none; cursor: pointer; position: relative; overflow: hidden; transition: transform 0.2s; }
    .ce-submit::before { content: ''; position: absolute; inset: 0; background: #fff; transform: translateY(101%); transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
    .ce-submit:hover::before { transform: translateY(0); }
    .ce-submit span { position: relative; z-index: 1; }
    .ce-submit:active { transform: scale(0.98); }

    .ce-footer { background: #074F4F; padding: 80px 48px 32px; }
    .ce-f-inner { max-width: 1200px; margin: 0 auto; }
    .ce-f-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px; }
    .ce-f-brand-name { font-family: 'Cormorant', Georgia, serif; font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: #FAF7F2; margin-bottom: 14px; }
    .ce-f-brand p, .ce-footer p { font-family: 'Inter', sans-serif !important; font-size: 13px !important; line-height: 1.7 !important; color: rgba(250,247,242,0.4) !important; max-width: 260px; }
    .ce-f-social { display: flex; gap: 16px; margin-top: 24px; }
    .ce-f-social a { width: 36px; height: 36px; border: 1px solid rgba(198,167,105,0.25); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Inter', sans-serif; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: rgba(250,247,242,0.5); text-decoration: none; transition: all 0.3s; }
    .ce-f-social a:hover { background: #C6A769; border-color: #C6A769; color: #074F4F; }
    .ce-f-col h5 { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: #C6A769; margin-bottom: 20px; }
    .ce-f-col ul { list-style: none; padding: 0; margin: 0; }
    .ce-f-col li { margin-bottom: 12px; }
    .ce-f-col a { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(250,247,242,0.4); text-decoration: none; transition: color 0.3s; }
    .ce-f-col a:hover { color: #C6A769; }
    .ce-f-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(198,167,105,0.2), transparent); margin-bottom: 32px; }
    .ce-f-bottom { display: flex; justify-content: space-between; font-family: 'Inter', sans-serif; font-size: 11px; color: rgba(250,247,242,0.25); }
    .ce-f-bottom a { color: rgba(250,247,242,0.25); margin-left: 20px; text-decoration: none; transition: color 0.3s; }
    .ce-f-bottom a:hover { color: #C6A769; }

    @media (max-width: 1024px) { .ce-nav-links { display: none; } .ce-f-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 600px) { .ce-card { padding: 32px 24px; } .ce-heading { font-size: 28px; } .ce-nav-inner { padding: 20px 24px; } .ce-footer { padding: 60px 24px 24px; } .ce-f-grid { grid-template-columns: 1fr; } }
  `;

  const CheckIcon = () => (
    <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5"/></svg>
  );

  return (
    <div>
      <style>{css}</style>

      <nav className="ce-nav">
        <div className="ce-nav-inner">
          <a href="/" className="ce-nav-logo">Aware</a>
          <ul className="ce-nav-links">
            <li><a href="/">Acasă</a></li>
            <li><a href="/despre-noi">Despre noi</a></li>
            <li><a href="/servicii">Servicii</a></li>
            <li><a href="/evenimente">Evenimente</a></li>
            <li><a href="/#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      <div className={"ce-scene" + (loaded ? " loaded" : "")}>
        <div className="ce-grain" />
        <div className="ce-content">
          <div className="ce-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h1 className="ce-heading">Confirmă adresa de email</h1>
          <p className="ce-sub">
            Te rugăm să confirmi adresa de email <strong>{maskedEmail}</strong> pentru a-ți trimite un cod de verificare.
          </p>

          <div className="ce-card">
            <form noValidate onSubmit={handleSubmit}>
              <label className="ce-label">Adresa de email</label>
              <input
                className="ce-input"
                type="text"
                value={enteredEmail}
                onChange={(e) => { setEnteredEmail(e.target.value); if (!emailTouched) setEmailTouched(true); setError(""); }}
                onFocus={() => { if (!emailTouched) setEmailTouched(true); }}
                placeholder="exemplu@email.com"
                required
              />

              {emailTouched && enteredEmail.length > 0 && (
                <div className="ce-checks">
                  <div className={"ce-check" + (emailHasAt ? " valid" : "")}>
                    <span className="ce-check-icon"><CheckIcon /></span>
                    Conține simbolul @
                  </div>
                  <div className={"ce-check" + (emailValid ? " valid" : "")}>
                    <span className="ce-check-icon"><CheckIcon /></span>
                    Adresă de email validă
                  </div>
                </div>
              )}

              {error && <div className="ce-error">{error}</div>}

              <button type="submit" className="ce-submit"><span>Trimite codul</span></button>
            </form>
          </div>
        </div>
      </div>

      <footer className="ce-footer">
        <div className="ce-f-inner">
          <div className="ce-f-grid">
            <div>
              <div className="ce-f-brand-name">Aware</div>
              <p>Un spațiu sigur pentru conștientizare, echilibru și sprijin emoțional.</p>
              <div className="ce-f-social"><a href="#">Ig</a><a href="#">Fb</a><a href="#">Tk</a></div>
            </div>
            <div className="ce-f-col"><h5>Servicii</h5><ul><li><a href="#">Terapie Individuală</a></li><li><a href="#">Terapie de Cuplu</a></li><li><a href="#">Familie &amp; Parenting</a></li><li><a href="#">Anxietate &amp; Depresie</a></li></ul></div>
            <div className="ce-f-col"><h5>Terapeuți</h5><ul><li><a href="#">Alexandra Strambeanu</a></li><li><a href="#">Luna Cosmari</a></li><li><a href="#">Bianca Istratescu</a></li><li><a href="#">Kathrin Moculity</a></li></ul></div>
            <div className="ce-f-col"><h5>Aware</h5><ul><li><a href="/despre-noi">Despre noi</a></li><li><a href="/#contact">Contact</a></li><li><a href="/evenimente">Evenimente</a></li><li><a href="/blog">Blog</a></li></ul></div>
          </div>
          <div className="ce-f-divider"></div>
          <div className="ce-f-bottom">
            <span>© 2025 Aware — Centru de Psihoterapie</span>
            <div><a href="/conditii">Termeni</a><a href="/confidentialitate">Confidențialitate</a></div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConfirmEmail;
